import logger from "../utils/logger";
import { API_BASE_URL, apiUrl } from '../config/appConfig';
import {
  clearStoredAdminToken,
  clearStoredMarketingToken,
  clearStoredUserAuthTokens,
  getStoredAuthToken,
  getStoredMarketingToken,
  setStoredAuthToken
} from '../utils/authTokens';

const MARKETING_LOGIN_PATH = '/marketing/login';

const isMarketingPath = () => (
  typeof window !== 'undefined' &&
  (window.location?.pathname || '').startsWith('/marketing')
);

// Sends the user back to the page they were on after signing in again.
const redirectToMarketingLogin = () => {
  clearStoredMarketingToken();
  const { pathname, search } = window.location;
  if (pathname !== MARKETING_LOGIN_PATH) {
    window.location.href = `${MARKETING_LOGIN_PATH}?next=${encodeURIComponent(`${pathname}${search}`)}`;
  }
};

// Builds an Error carrying the HTTP status and parsed DRF body so callers can
// show field-level validation errors or special-case 429.
const buildHttpError = async (response) => {
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    // Non-JSON error body
  }

  let message = `HTTP error! status: ${response.status}`;
  if (data && typeof data === 'object') {
    if (typeof data.detail === 'string') {
      message = data.detail;
    } else if (typeof data.error === 'string') {
      message = data.error;
    } else {
      const fieldErrors = Object.keys(data).map((field) => (
        `${field}: ${Array.isArray(data[field]) ? data[field].join(', ') : data[field]}`
      ));
      if (fieldErrors.length > 0) {
        message = fieldErrors.join('; ');
      }
    }
  }

  const error = new Error(message);
  error.status = response.status;
  error.data = data;
  return error;
};

// Query string for the leads list/export; empty filters are left out.
const leadQuery = (filters) => {
  const params = new URLSearchParams();
  ['stage', 'range', 'consent', 'search', 'page'].forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  return params;
};

const parseJsonResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
    return {};
  }
  return response.json();
};

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = getStoredAuthToken();
  }

  getToken() {
    this.token = getStoredAuthToken();
    return this.token;
  }

  setToken(token, options = {}) {
    this.token = token;
    setStoredAuthToken(token, options);
  }

  removeToken({ admin = false } = {}) {
    this.token = null;
    if (admin) {
      clearStoredAdminToken();
      return;
    }

    clearStoredUserAuthTokens();
  }

  async makeRequest(endpoint, options = {}) {
    const url = apiUrl(endpoint);
    const token = this.getToken();
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === 'object') {
            // Handle field-specific errors
            const fieldErrors = [];
            Object.keys(errorData).forEach(field => {
              if (Array.isArray(errorData[field])) {
                fieldErrors.push(`${field}: ${errorData[field].join(', ')}`);
              } else {
                fieldErrors.push(`${field}: ${errorData[field]}`);
              }
            });
            if (fieldErrors.length > 0) {
              errorMessage = fieldErrors.join('; ');
            }
          }
        } catch (e) {
          // If we can't parse the error response, keep the generic message
        }
        
        if (response.status === 401) {
          // Check if we're in admin context
          if (isMarketingPath()) {
            redirectToMarketingLogin();
          } else if (window.location.pathname.includes('/admin/')) {
            this.removeToken({ admin: true });
            window.location.href = '/admin/login';
          } else {
            this.removeToken();
            window.location.href = '/login';
          }
        }
        
        throw new Error(errorMessage);
      }
      
      // Handle empty responses (like 204 No Content from DELETE requests)
      const contentType = response.headers.get('content-type');
      if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
        return {}; // Return empty object for successful requests with no content
      }
      
      return await response.json();
    } catch (error) {
      logger.error('API request failed:', error);
      throw error;
    }
  }

  getFilenameFromDisposition(contentDisposition) {
    if (!contentDisposition) return null;

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1].replace(/"/g, ''));
    }

    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return filenameMatch?.[1] || null;
  }

  async downloadRequest(endpoint, options = {}) {
    const url = apiUrl(endpoint);
    const token = this.getToken();
    const response = await fetch(url, {
      ...options,
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return response.json();
    }

    // Guard against downloading HTML/error pages as files.
    if (contentType.includes('text/html')) {
      throw new Error('Invalid file response: server returned HTML instead of a file.');
    }

    return {
      blob: await response.blob(),
      file_name: this.getFilenameFromDisposition(response.headers.get('content-disposition')),
      content_type: contentType
    };
  }

  // Authentication APIs
  async register(userData) {
    return this.makeRequest('/api/auth/register/', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async login(credentials) {
    return this.makeRequest('/api/auth/login/', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async logout() {
    return this.makeRequest('/api/auth/logout/', {
      method: 'POST',
    });
  }

  // Email verification APIs
  async sendVerificationEmail(emailData) {
    return this.makeRequest('/api/auth/send-verification/', {
      method: 'POST',
      body: JSON.stringify(emailData),
    });
  }

  async verifyEmail(verificationData) {
    return this.makeRequest('/api/auth/verify-email/', {
      method: 'POST',
      body: JSON.stringify(verificationData),
    });
  }

  async getProfile() {
    return this.makeRequest('/api/users/profile/');
  }

  async getUserProfile() {
    return this.getProfile();
  }

  // Contract APIs
  async getContracts({ page = 1, pageSize = 10 } = {}) {
    const queryParams = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    }).toString();

    return this.makeRequest(`/api/contracts/?${queryParams}`);
  }

  async getContract(contractId) {
    return this.makeRequest(`/api/contracts/${contractId}/`);
  }

  async getAmendments(contractId) {
    return this.makeRequest(`/api/contracts/${contractId}/amendments/`);
  }

  async getDeviationRisk(contractId) {
    return this.makeRequest(`/api/contracts/${contractId}/deviation_risk/`);
  }

  async getMainSummary(contractId) {
    return this.makeRequest(`/api/contracts/${contractId}/main_summary/`);
  }

  async getHighlightedPdf(contractId) {
    return this.makeRequest(`/api/contracts/${contractId}/highlighted_pdf/`);
  }

  async getOriginalContractFile(contractId) {
    return this.downloadRequest(`/api/contracts/${contractId}/download/`);
  }

  async deleteContract(contractId) {
    return this.makeRequest(`/api/contracts/${contractId}/`, {
      method: 'DELETE',
    });
  }

  async getContractDetails(contractId) {
    return this.makeRequest(`/api/contracts/${contractId}/`);
  }

  // Note: This method is replaced by the more complete uploadContract method below

  async analyzeContract(contractId) {
    return this.makeRequest(`/api/contracts/${contractId}/analyze/`, {
      method: 'POST',
    });
  }

  // Chat APIs
  async getChatSessions() {
    return this.makeRequest('/chat/api/sessions/');
  }

  async createChatSession(contractId, sessionName) {
    return this.makeRequest('/chat/api/sessions/', {
      method: 'POST',
      body: JSON.stringify({
        contract_id: contractId,
        session_name: sessionName,
      }),
    });
  }

  async getChatMessages(sessionId) {
    return this.makeRequest(`/chat/api/sessions/${sessionId}/messages/`);
  }

  async sendMessage(sessionId, content) {
    return this.makeRequest(`/chat/api/sessions/${sessionId}/send_message/`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  // Additional APIs for new features
  async getUserSubscription() {
    return this.makeRequest('/api/auth/subscription/');
  }

  async getContractAnalysis(contractId) {
    return this.makeRequest(`/contracts/api/contracts/${contractId}/analysis/`);
  }

  async generateAmendments(contractId) {
    return this.makeRequest(`/contracts/api/contracts/${contractId}/amendments/`, {
      method: 'POST',
    });
  }

  async updateUserProfile(profileData) {
    return this.makeRequest('/api/users/update_profile/', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async changePassword(passwordData) {
    return this.makeRequest('/api/auth/change-password/', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });
  }

  async uploadProfileImage(formData) {
    const url = apiUrl('/api/users/upload_profile_image/');
    const token = this.getToken();
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
          // Don't set Content-Type, let browser handle it for FormData
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // If we can't parse the error response, keep the generic message
        }
        
        throw new Error(errorMessage);
      }
      
      return await response.json();
    } catch (error) {
      logger.error('Profile image upload failed:', error);
      throw error;
    }
  }

  async getCreditTransactions() {
    return this.makeRequest('/contracts/api/credit-transactions/');
  }

  // General Chat API for non-contract specific conversations
  async sendGeneralChatMessage(message) {
    return this.makeRequest('/chat/api/general-chat/', {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  // Vector Database APIs
  async storeVectors(vectors, indexName = 'jelani-contracts') {
    return this.makeRequest('/api/vector-db/store/', {
      method: 'POST',
      body: JSON.stringify({ vectors, indexName }),
    });
  }

  async searchVectors(vector, topK = 5, filter = {}, indexName = 'jelani-contracts') {
    return this.makeRequest('/api/vector-db/search/', {
      method: 'POST',
      body: JSON.stringify({ vector, topK, filter, indexName }),
    });
  }

  async deleteVectors(filter, indexName = 'jelani-contracts') {
    return this.makeRequest('/api/vector-db/delete/', {
      method: 'DELETE',
      body: JSON.stringify({ filter, indexName }),
    });
  }

  async getVectorStats() {
    return this.makeRequest('/api/vector-db/stats/');
  }

  async generateEmbeddings(text) {
    return this.makeRequest('/api/vector-db/embeddings/', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  async generateRAGContext(query, contractId = null, maxTokens = 2000) {
    return this.makeRequest('/api/vector-db/rag-context/', {
      method: 'POST',
      body: JSON.stringify({ query, contractId, maxTokens }),
    });
  }

  async uploadContract(file, metadata = {}, onProgress = null) {
    // Create FormData and append file and metadata
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', metadata.title || file.name);
    formData.append('description', metadata.description || '');
    formData.append('contract_type', metadata.contract_type || 'general');
    
    const url = apiUrl('/api/contracts/upload/');
    const token = this.getToken();
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && typeof onProgress === 'function') {
          const percentComplete = (e.loaded / e.total) * 100;
          onProgress(percentComplete);
        }
      });
      
      xhr.open('POST', url);
      // Set auth header if present
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      // Increase timeout to handle large files and processing latency
      xhr.timeout = 10 * 60 * 1000; // 10 minutes

      xhr.onerror = () => {
        const msg = xhr.status ? `HTTP ${xhr.status}: ${xhr.statusText || 'Error'}` : 'Network error';
        reject(new Error(msg));
      };
      xhr.ontimeout = () => reject(new Error('Upload timed out'));

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch (e) {
            resolve({});
          }
        } else {
          let errorMessage = `HTTP ${xhr.status}: ${xhr.statusText || 'Error'}`;
          try {
            const errorData = JSON.parse(xhr.responseText);
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.detail) {
              errorMessage = errorData.detail;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          } catch {}
          reject(new Error(errorMessage));
        }
      };

      xhr.send(formData);
    });
  }

  // Admin APIs
  async adminLogin(credentials) {
    return this.makeRequest('/api/admin/login/', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async getTableData(tableName, filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    
    // Direct API call with better error handling for admin endpoints
    const endpoint = `/api/admin/table/${tableName}/`;
    const url = queryParams ? `${apiUrl(endpoint)}?${queryParams}` : apiUrl(endpoint);
    const token = this.getToken();
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        
        if (errorData.detail) {
          throw new Error(errorData.detail);
        } else if (errorData.error) {
          throw new Error(errorData.error);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }
      
      return await response.json();
    } catch (error) {
      logger.error('Direct table data request failed:', error);
      throw error;
    }
  }

  async addPayment(paymentData) {
    return this.makeRequest('/api/admin/add-payment/', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  // Marketing staff accounts (admin token required). Passwords are sent, never returned.
  async getAdminMarketingUsers() {
    return this.makeRequest('/api/admin/marketing-users/');
  }

  async createAdminMarketingUser(data) {
    return this.makeRequest('/api/admin/marketing-users/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminMarketingUser(id, data) {
    return this.makeRequest(`/api/admin/marketing-users/${encodeURIComponent(id)}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getPaymentAnalytics(dateRange = 'all') {
    return this.makeRequest(`/api/admin/analytics/?range=${dateRange}`);
  }

  async getPaymentsData(userId) {
    try {
      const token = this.getToken();
      // Use the user endpoint instead of admin endpoint
      const response = await fetch(apiUrl('/api/users/payments/'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Payment data request failed:', errorText);
        throw new Error(`Failed to fetch payments: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error fetching payment data:', error);
      throw error;
    }
  }

  // Contract Chat APIs
  async getChatHistory(contractId, options = {}) {
    const params = new URLSearchParams();

    if (options.summaryOnly) {
      params.set('summary_only', 'true');
    }

    if (options.chatId) {
      params.set('chat_id', options.chatId);
    }

    const queryString = params.toString();
    return this.makeRequest(`/api/chat/history/${contractId}/${queryString ? `?${queryString}` : ''}`);
  }

  async saveChatToContract(contractId, chatHistory) {
    return this.makeRequest(`/api/chat/history/${contractId}/`, {
      method: 'PUT',
      body: JSON.stringify({ chat_history: chatHistory }),
    });
  }

  async deleteChatSession(contractId, chatId) {
    const params = new URLSearchParams({ chat_id: chatId });
    return this.makeRequest(`/api/chat/history/${contractId}/?${params.toString()}`, {
      method: 'DELETE',
    });
  }

  async sendChatMessage(contractId, message, chatId = null) {
    return this.makeRequest('/api/chat/send/', {
      method: 'POST',
      body: JSON.stringify({
        contract_id: contractId,
        message: message,
        ...(chatId ? { chat_id: String(chatId) } : {}),
      }),
    });
  }

  // Password Reset APIs
  async sendPasswordResetCode(data) {
    return this.makeRequest('/api/auth/password-reset/send-code/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async verifyPasswordResetCode(data) {
    return this.makeRequest('/api/auth/password-reset/verify-code/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resetPassword(data) {
    return this.makeRequest('/api/auth/password-reset/reset/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteUserData() {
    return this.makeRequest('/api/users/delete_user_data/', {
      method: 'DELETE',
    });
  }

  // Marketing APIs: only ever send the marketing token (never customer/admin tokens).
  async marketingRequest(endpoint, { redirectOn401 = true, ...options } = {}) {
    const token = getStoredMarketingToken();
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;

    try {
      const response = await fetch(apiUrl(endpoint), {
        ...options,
        headers: {
          ...(!isFormData && { 'Content-Type': 'application/json' }),
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options.headers,
        },
      });

      if (!response.ok) {
        const error = await buildHttpError(response);
        if (response.status === 401 && redirectOn401) {
          redirectToMarketingLogin();
        }
        throw error;
      }

      return await parseJsonResponse(response);
    } catch (error) {
      logger.error('Marketing API request failed:', error);
      throw error;
    }
  }

  async marketingLogin(credentials) {
    return this.marketingRequest('/api/marketing/login/', {
      method: 'POST',
      body: JSON.stringify(credentials),
      redirectOn401: false,
    });
  }

  async marketingMe() {
    return this.marketingRequest('/api/marketing/me/');
  }

  marketingDashboardRequest(name, range = '30d') {
    const params = new URLSearchParams({ range });
    return this.marketingRequest(`/api/marketing/${name}/?${params.toString()}`);
  }

  async getMarketingOverview(range) {
    return this.marketingDashboardRequest('overview', range);
  }

  async getMarketingSources(range) {
    return this.marketingDashboardRequest('sources', range);
  }

  async getMarketingCountries(range) {
    return this.marketingDashboardRequest('countries', range);
  }

  async getMarketingDevices(range) {
    return this.marketingDashboardRequest('devices', range);
  }

  async getMarketingPages(range) {
    return this.marketingDashboardRequest('pages', range);
  }

  async getMarketingFunnel(range) {
    return this.marketingDashboardRequest('funnel', range);
  }

  async getMarketingCampaigns(range) {
    return this.marketingDashboardRequest('campaigns', range);
  }

  // Latest events; not tied to the selected date range.
  async getMarketingActivity() {
    return this.marketingRequest('/api/marketing/activity/');
  }

  async getMarketingLeads(filters = {}) {
    return this.marketingRequest(`/api/marketing/leads/?${leadQuery(filters).toString()}`);
  }

  // Excel (.xlsx) file of every lead matching the filters; resolves to { blob, filename }.
  async exportMarketingLeads(filters = {}) {
    const token = getStoredMarketingToken();
    const params = leadQuery(filters);
    params.delete('page');
    const response = await fetch(apiUrl(`/api/marketing/leads/export/?${params.toString()}`), {
      headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
    });
    if (!response.ok) {
      const error = await buildHttpError(response);
      if (response.status === 401) {
        redirectToMarketingLogin();
      }
      throw error;
    }
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    return { blob: await response.blob(), filename: match ? match[1] : 'jelani-leads.xlsx' };
  }

  // Public: the token comes from the unsubscribe link in a marketing email.
  async unsubscribeFromMarketing(token) {
    const response = await fetch(apiUrl('/api/marketing/unsubscribe/'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (!response.ok) {
      throw await buildHttpError(response);
    }
    return parseJsonResponse(response);
  }

  // state: '' (all), 'draft', 'scheduled' or 'live'.
  async getMarketingBlogPosts({ page = 1, state = '' } = {}) {
    const params = new URLSearchParams({ page: String(page) });
    if (state) params.set('state', state);
    return this.marketingRequest(`/api/marketing/blog/posts/?${params.toString()}`);
  }

  async getMarketingBlogPost(id) {
    return this.marketingRequest(`/api/marketing/blog/posts/${encodeURIComponent(id)}/`);
  }

  // Blog write methods take request options so the editor can pass
  // { redirectOn401: false } and keep unsaved text when the session expires.
  async createMarketingBlogPost(data, options = {}) {
    return this.marketingRequest('/api/marketing/blog/posts/', {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMarketingBlogPost(id, data, options = {}) {
    return this.marketingRequest(`/api/marketing/blog/posts/${encodeURIComponent(id)}/`, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteMarketingBlogPost(id, options = {}) {
    return this.marketingRequest(`/api/marketing/blog/posts/${encodeURIComponent(id)}/`, {
      ...options,
      method: 'DELETE',
    });
  }

  // publishAt: an ISO time with a zone to schedule; omit to publish now.
  async publishMarketingBlogPost(id, { publishAt, ...options } = {}) {
    return this.marketingRequest(`/api/marketing/blog/posts/${encodeURIComponent(id)}/publish/`, {
      ...options,
      method: 'POST',
      ...(publishAt && { body: JSON.stringify({ publish_at: publishAt }) }),
    });
  }

  async unpublishMarketingBlogPost(id, options = {}) {
    return this.marketingRequest(`/api/marketing/blog/posts/${encodeURIComponent(id)}/unpublish/`, {
      ...options,
      method: 'POST',
    });
  }

  async uploadMarketingBlogCover(id, file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    return this.marketingRequest(`/api/marketing/blog/posts/${encodeURIComponent(id)}/cover/`, {
      ...options,
      method: 'POST',
      body: formData,
    });
  }

  // Uploads an image for a post's Markdown content. Resolves to { url }.
  async uploadMarketingBlogImage(id, file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    return this.marketingRequest(`/api/marketing/blog/posts/${encodeURIComponent(id)}/images/`, {
      ...options,
      method: 'POST',
      body: formData,
    });
  }

  // Public APIs: never send any token and never redirect on 401.
  // Content-Type only goes with a JSON body: on a bodiless cross-origin GET it
  // would force a CORS preflight request.
  async publicRequest(endpoint, options = {}) {
    const hasJsonBody = options.body != null
      && !(typeof FormData !== 'undefined' && options.body instanceof FormData);
    const response = await fetch(apiUrl(endpoint), {
      ...options,
      headers: {
        ...(hasJsonBody && { 'Content-Type': 'application/json' }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw await buildHttpError(response);
    }

    return parseJsonResponse(response);
  }

  async getBlogPosts({ page = 1 } = {}) {
    const params = new URLSearchParams({ page: String(page) });
    return this.publicRequest(`/api/blog/posts/?${params.toString()}`);
  }

  async getBlogPost(slug) {
    return this.publicRequest(`/api/blog/posts/${encodeURIComponent(slug)}/`);
  }

  // First-party page view tracking. Fire-and-forget: never throws.
  async trackPageView(payload) {
    try {
      await fetch(apiUrl('/api/analytics/track/'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      });
    } catch (error) {
      // Tracking must never surface errors.
    }
  }
}

export default new ApiService();
